import React from "react";
import { Link } from "react-router-dom";
import { getLeagueStatus, previewStatus } from "./leagueStatus";
import { organizerHandle, previewMetaParts, previewSecondLine } from "./leagueFormat";
import { ChevronRightIcon, AvatarPersonIcon } from "./icons";
import useMinuteTick from "./useMinuteTick";
import { competitionDestination } from "../../../utils/leaguePath";

// THE league preview card. One design for the whole site: League Play home's
// Public Leagues and My Leagues sections, the browse lists, the filter screen.
// A league must not look like two different things depending on which screen
// found it.
//
// Four lines, in the order someone reads them:
//
//   1. the organiser's picture and HANDLE, with the league's state right-aligned
//      inline with it — "Starts in 18h" (teal), "Ends in 18h" (green),
//      "Ended Aug 8" (grey), or the quiet Pending approval pill
//   2. the league title, with the chevron that says this row opens
//   3. the tagline before it starts; the leading return once it's live; the
//      winning return once it's over
//   4. the metadata line
//
// The description field never appears here — it belongs to the league's own
// screens and is far longer than a card can carry.
//
// There is no per-screen variation: a private league leads its metadata with
// "Private League" and a public one with its experience level, wherever the row
// is rendered. See previewMetaParts.
//
// Type hierarchy, darkest to lightest — four steps, each one clearly a step
// down, so the eye reads them in order:
//
//   title             #0F172A
//   second line       #4B5563
//   metadata primary  #6B7280
//   joinability       #9CA3AF
// How far a row's hover state has to reach past its own content box.
//
// Client Rev3: "why does our highlights not highlight the entire card or league
// within a divider line. when you hover or tap it highlights everything but the
// two edges. looks weird and feels unfinished."
//
// Exactly right, and the cause is that the PANEL carries the horizontal padding
// while the ROW carries the hover background. The background can only paint the
// row's own box, so the panel's gutters — 16px on the phone, 20px on desktop —
// stay white on either side and the highlight reads as a floating stripe.
//
// A row therefore bleeds its box out to the panel's padding edge and pays the
// same padding back, so the CONTENT does not move a pixel and only the painted
// area grows. Two panels exist and they are padded differently, so the caller
// says which it is rather than this file guessing — a row that bleeds 16px
// inside a panel with no mobile padding would hang off the card.
//
//   PANEL_BLEED.inset  -> panels padded `px-4 md:px-5` (League Play home, My
//                         Leagues section, Find Games)
//   PANEL_BLEED.wide   -> panels padded `md:px-5` only, edge to edge on mobile
//                         (Public Leagues, My Leagues, Cash Games)
export const PANEL_BLEED = {
  inset: "-mx-4 px-4 md:-mx-5 md:px-5",
  wide: "md:-mx-5 md:px-5",
};

// WHERE THE ROW OPENS.
//
// Client, realigning to the original model: "Starting soon -> opens the
// league/game landing page (join screen with the entry CTA). Live now -> opens
// the leaderboard preview page we just built, with the metaline showing live
// league/game details. Completed -> 'View Results', opens that same leaderboard
// preview page. So once a competition goes live, the landing page is retired.
// Everything routes to the leaderboard preview + details preview. There is no
// live-state landing page with a CTA."
//
// And the rule that outranks all three, unchanged: "if you are a member and
// click a 'Starting soon' or 'Live now' League or Game ... it opens the league
// for the member, not the landing page or leaderboard preview - do not change."
//
// THE BUG THIS FIXES. This used to send a live league to the board only when
// `isEntryClosed(game)` was true, which is false whenever a league allows late
// entry. So a live, late-entry-open public league opened its LANDING page —
// "clicking a public league from the Live now section on League Play Home or
// View All currently opens the landing page with a muted 'Entry closed'
// dead-end CTA. It should route to the leaderboard preview page instead, same
// as every other live listing."
//
// The entry-open/entry-closed split is gone entirely: `live` is now enough on
// its own. Whether a live league still accepts entries is a question for the
// landing page's CTA, not for where a listing points.
//
// The member test is FIRST and explicit. It used to be implicit in
// isEntryClosed (which returns false for a member), and losing that test would
// have quietly sent members to the board for their own live leagues.
//
// /contest/<slug> is the league's front door: a member sees their league home,
// a visitor sees the landing page. That is the Starting-soon destination and
// the member destination both.
//
// `member` is an override for the screens that KNOW the answer without having to
// read it off the row. Client: "My Leagues is different - My Leagues Live now
// takes the user directly inside." Everything listed on My Leagues is a
// competition the reader is in, so that screen states it outright rather than
// depending on the list endpoint having stamped `isMember` on each row — a
// backend deploy is not something this rule should be able to lose.
//
// The state rule itself now lives in competitionDestination, shared with the
// cash-game listings — client: "All of league play should carry the same state
// logic, including cash games. We need to stay consistent so the system is easy
// to learn." The `member` override stays here because it is this screen's, not
// the rule's.
const rowDestination = (game, status, member) => {
  const slug = game?.vanity_url;
  if (!slug) return "/contest";
  if (member) return `/contest/${slug}`;
  return competitionDestination(game, status);
};

const PublicLeagueRow = ({ game, bleed = PANEL_BLEED.wide, member }) => {
  const status = getLeagueStatus(game);
  const chip = previewStatus(status, game);
  const second = previewSecondLine(game, status);
  const meta = previewMetaParts(game, status);

  useMinuteTick(!!status?.countdownValue);

  const avatar = game?.user?.image || "";

  return (
    <Link
      to={rowDestination(game, status, member || !!game?.isMember)}
      className={`group block py-4 transition-colors hover:bg-[#F8FAFA] ${bleed}`}
    >
      {/* Client Rev 4 set the row's skeleton, and it is the same on mobile and
          desktop:

            organizer_handle                    Ends in 5d
            League title                                 ›
            Experience · Asset · Duration · Joinability

          "The chevron stays pinned to the far right and is vertically centered
          against the overall league row, while the status sits above it in its
          own right aligned position." So the chevron is a COLUMN of the row, not
          something tucked onto the title line — `items-center` on this flex is
          what centres it against all three lines at once. Everything else lives
          in the content column beside it, which is why the status can be right
          aligned to a constant edge: "Keep that status column in the exact same
          position across all three tabs." Right-aligned means "Ends in 5d",
          "Starts in 3d" and "Ended Aug 22" all finish on the same line, whatever
          their length. */}
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <span className="flex min-w-0 items-center gap-2">
              {avatar ? (
                <img
                  src={avatar}
                  alt=""
                  className="h-5 w-5 shrink-0 rounded-full bg-[#F3F4F6] object-cover"
                  onError={(e) => {
                    e.currentTarget.style.visibility = "hidden";
                  }}
                />
              ) : (
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F3F4F6]">
                  <AvatarPersonIcon className="h-3 w-3" />
                </span>
              )}
              <span className="truncate text-sm text-secondary">
                {organizerHandle(game)}
              </span>
            </span>
            {chip?.text && (
              <span
                className={`shrink-0 text-right text-sm font-semibold ${
                  chip.pill ? "rounded-full px-2 py-0.5 font-medium" : ""
                } ${chip.cls}`}
              >
                {chip.text}
              </span>
            )}
          </div>

          <h3 className="mt-1.5 font-montserrat font-bold text-base md:text-lg text-[#0F172A] leading-snug">
            {game.title}
          </h3>

          {/* The league's own result if it has one, otherwise its pitch. Never
              both: this line answers "why do I care", and once there is a number
              on the board, the number is the answer. */}
          {second && (
            <p
              className="mt-1 overflow-hidden text-sm text-[#4B5563]"
              // Backstop for the tagline, which is capped at LIMITS.tagline so it
              // normally sits on one line. Written inline rather than with
              // Tailwind's `line-clamp-2`: this build's autoprefixer strips
              // `display:-webkit-box` and `-webkit-box-orient` as outdated
              // prefixes, so the emitted utility is `-webkit-line-clamp` alone —
              // which clamps nothing, and let a long second line grow the row
              // instead of ending it.
              style={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
              }}
            >
              {second.tagline || (
                <>
                  {second.label}{" "}
                  {/* Colour comes with the figure (returnDisplay), so a negative
                      number can never be painted green. */}
                  <span className={`font-semibold ${second.valueClass}`}>
                    {second.value}
                  </span>
                </>
              )}
            </p>
          )}

          {/* The separator before the joinability belongs to the STRONG half —
              the design shows "Advanced · Forex · 4-Day League ·" carrying the
              middot, with only the joinability itself left quiet. */}
          {meta.primary && (
            <p className="mt-2 text-xs">
              <span className="text-[#6B7280]">
                {meta.primary}
                {meta.join ? " · " : ""}
              </span>
              {meta.join && <span className="text-[#9CA3AF]">{meta.join}</span>}
            </p>
          )}
        </div>

        <ChevronRightIcon className="h-5 w-5 shrink-0 text-[#9CA3AF] transition-colors group-hover:text-[#0F4146]" />
      </div>
    </Link>
  );
};

export default PublicLeagueRow;
